import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Phone, MapPin, Clock, Facebook, Send, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO, { LOCATION_KEYWORDS } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate simple math captcha
const generateCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  return { num1, num2, answer: num1 + num2 };
};

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    postcode: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setCaptchaInput("");
  }, []);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (honeypot) {
      toast.error("Something went wrong. Please try again.");
      return;
    }

    // Captcha validation
    if (parseInt(captchaInput) !== captcha.answer) {
      toast.error("Incorrect answer. Please solve the maths question.");
      refreshCaptcha();
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      navigate("/success");
    } catch {
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
      refreshCaptcha();
    }
  };

  const areas = [
    "Motherwell",
    "Bellshill",
    "Hamilton",
    "Wishaw",
    "Glasgow",
    "Lanarkshire",
    "Newton Mearns",
    "East Kilbride",
    "Blantyre",
    "Coatbridge",
    "Airdrie",
    "Uddingston",
  ];

  return (
    <div data-testid="contact-page" className="pt-32 lg:pt-36">
      <SEO 
        title="Contact Us | Free Quote Double Glazing Motherwell, Hamilton, Glasgow, Lanarkshire"
        description="Get a free quote for double glazing, uPVC windows, composite doors & garden rooms in Motherwell, Hamilton, Glasgow & Lanarkshire. Fast response, no obligation. Call 07917 181597"
        keywords={`contact double glazing, free quote windows, free quote doors, free quote Motherwell, free quote Hamilton, double glazing quote Glasgow, double glazing quote Lanarkshire, ${LOCATION_KEYWORDS}`}
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            CONTACT US FOR A{" "}
              <span className="text-[#FF6B00]">FREE QUOTE</span>
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Contact us for a free, no-obligation quote. We're here to help with
            all your <Link to="/windows" className="text-[#FF6B00] hover:underline">double glazing</Link>, <Link to="/doors" className="text-[#FF6B00] hover:underline">door</Link>, and <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden room</Link> needs. See our <Link to="/recent-work" className="text-[#FF6B00] hover:underline">recent work</Link> and <Link to="/reviews" className="text-[#FF6B00] hover:underline">customer reviews</Link>.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2
                className="text-2xl font-black text-[#0A0A0A] mb-8"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                GET IN TOUCH
              </h2>

              <div className="space-y-6">
                <a
                  href="tel:07917181597"
                  data-testid="contact-phone"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 bg-[#FF6B00] flex items-center justify-center flex-shrink-0 group-hover:bg-[#E65000] transition-colors">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0A0A0A] uppercase text-sm tracking-wide">
                      Phone
                    </p>
                    <p className="text-[#525252] text-lg">07917 181597</p>
                  </div>
                </a>

                <a
                  href="mailto:c.glanceyglaziers@gmail.com"
                  data-testid="contact-email"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 bg-[#FF6B00] flex items-center justify-center flex-shrink-0 group-hover:bg-[#E65000] transition-colors">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0A0A0A] uppercase text-sm tracking-wide">
                      Email
                    </p>
                    <p className="text-[#525252]">c.glanceyglaziers@gmail.com</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0A0A0A] uppercase text-sm tracking-wide">
                      Service Area
                    </p>
                    <p className="text-[#525252]">
                      Lanarkshire, Glasgow & surrounding areas
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0A0A0A] uppercase text-sm tracking-wide">
                      Emergency
                    </p>
                    <p className="text-[#525252]">Quick Response Times</p>
                  </div>
                </div>

                <a
                  href="https://www.facebook.com/CGlanceyJoinersGlazing"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 bg-[#1877F2] flex items-center justify-center flex-shrink-0 group-hover:bg-[#166FE5] transition-colors">
                    <Facebook className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0A0A0A] uppercase text-sm tracking-wide">
                      Facebook
                    </p>
                    <p className="text-[#525252]">Follow us for updates</p>
                  </div>
                </a>
              </div>

              {/* Areas We Cover */}
              <div className="mt-12">
                <h3
                  className="text-lg font-black text-[#0A0A0A] mb-4"
                  style={{ fontFamily: "'Chivo', sans-serif" }}
                >
                  AREAS WE COVER
                </h3>
                <div className="flex flex-wrap gap-2">
                  {areas.map((area) => (
                    <span
                      key={area}
                      className="px-3 py-1 bg-[#F5F5F5] text-[#525252] text-sm"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#F5F5F5] p-8 md:p-12">
                <h2
                  className="text-2xl font-black text-[#0A0A0A] mb-2"
                  style={{ fontFamily: "'Chivo', sans-serif" }}
                >
                  REQUEST A <span className="text-[#FF6B00]">FREE QUOTE</span>
                </h2>
                <p className="text-[#525252] mb-8">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>

                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label block mb-2">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        data-testid="input-first-name"
                        className="w-full bg-white border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="form-label block mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        data-testid="input-last-name"
                        className="w-full bg-white border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="form-label block mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="input-email"
                        className="w-full bg-white border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="form-label block mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="input-phone"
                        className="w-full bg-white border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="form-label block mb-2">Postcode *</label>
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleChange}
                        required
                        data-testid="input-postcode"
                        className="w-full bg-white border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="form-label block mb-2">Service Required *</label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        data-testid="select-service"
                        className="w-full bg-white border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors"
                      >
                        <option value="">Select a service</option>
                        <option value="double-glazing">Double Glazing</option>
                        <option value="doors">Doors</option>
                        <option value="outbuilds">Garden Rooms / Outbuilds</option>
                        <option value="repairs">Glazing Repairs</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="form-label block mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      data-testid="textarea-message"
                      className="w-full bg-white border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors resize-none"
                      placeholder="Tell us more about your requirements..."
                    />
                  </div>

                  {/* Honeypot field - hidden from users, catches bots */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  {/* Simple Math Captcha */}
                  <div className="mt-6 p-4 bg-white border border-[#E5E5E5]">
                    <div className="flex items-center gap-3 mb-3">
                      <ShieldCheck className="w-5 h-5 text-[#FF6B00]" />
                      <label className="form-label font-bold text-[#0A0A0A]">Security Check *</label>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-bold text-[#0A0A0A] bg-[#F5F5F5] px-4 py-2 border border-[#E5E5E5]">
                        {captcha.num1} + {captcha.num2} = ?
                      </span>
                      <input
                        type="number"
                        value={captchaInput}
                        onChange={(e) => setCaptchaInput(e.target.value)}
                        required
                        data-testid="captcha-input"
                        placeholder="Answer"
                        className="w-24 bg-white border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-2 outline-none transition-colors text-center"
                      />
                      <button
                        type="button"
                        onClick={refreshCaptcha}
                        className="text-sm text-[#525252] hover:text-[#FF6B00] underline"
                      >
                        New question
                      </button>
                    </div>
                  </div>

                  {/* No Marketing Disclaimer */}
                  <div className="mt-6 p-4 bg-[#FFF5F0] border border-[#FFD4BD]">
                    <p className="text-sm text-[#525252]">
                      <strong className="text-[#0A0A0A]">Please note:</strong> This form is for customers enquiring about our double glazing services only. 
                      <span className="text-[#FF6B00] font-medium"> Please do not contact us with any marketing offers.</span>
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-button"
                    className="mt-8 bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="spinner" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Submit Request <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="bg-[#FF6B00] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl font-black text-white mb-2"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            NEED GLAZING REPAIRS?
          </h2>
          <p className="text-white/90 mb-6">
            Need urgent glazing repairs? Get in touch for a quick response.
          </p>
          <a
            href="tel:07917181597"
            data-testid="emergency-call"
            className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-all"
          >
            <Phone className="w-5 h-5" />
            Call Now: 07917 181597
          </a>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2
                className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-6"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                FIND US IN <span className="text-[#FF6B00]">LANARKSHIRE</span>
              </h2>
              <p className="text-[#525252] mb-4">
                C.Glancey Double Glazing is based in Lanarkshire, serving homeowners throughout the region and into Glasgow. 
                Our central location allows us to provide prompt service to all areas we cover, with most quotes being 
                completed within 48 hours of your initial enquiry.
              </p>
              <p className="text-[#525252] mb-6">
                We operate throughout North and South Lanarkshire, covering towns including Motherwell, Hamilton, East Kilbride, 
                Wishaw, Airdrie, Coatbridge, Bellshill, Uddingston, Blantyre, and Larkhall. We also serve customers in Glasgow, 
                Newton Mearns, Giffnock, Rutherglen, Cambuslang, and surrounding areas.
              </p>
              <div className="bg-[#F5F5F5] p-6">
                <h3 className="font-bold text-[#0A0A0A] mb-3">Our Service Area Includes:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-[#525252]">
                  <div>• Motherwell</div>
                  <div>• Hamilton</div>
                  <div>• East Kilbride</div>
                  <div>• Wishaw</div>
                  <div>• Airdrie</div>
                  <div>• Coatbridge</div>
                  <div>• Bellshill</div>
                  <div>• Uddingston</div>
                  <div>• Blantyre</div>
                  <div>• Larkhall</div>
                  <div>• Glasgow</div>
                  <div>• Newton Mearns</div>
                </div>
                <p className="text-sm text-[#525252] mt-4 italic">
                  Not sure if we cover your area? Give us a call and we'll let you know.
                </p>
              </div>
            </div>
            <div>
              <div className="h-80 lg:h-full min-h-[400px] bg-[#F5F5F5] border border-[#E5E5E5]">
                <iframe
                  title="C.Glancey Double Glazing Location - Lanarkshire"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d286258.4999999999!2d-4.1!3d55.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4888469617bd6bb7%3A0x1c09f883a0d7f5f4!2sLanarkshire!5e0!3m2!1sen!2suk!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="google-map"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Us Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-white mb-8 text-center"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            WHY CHOOSE <span className="text-[#FF6B00]">C.GLANCEY</span> DOUBLE GLAZING?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Free Home Survey",
                desc: "We visit your property at a time that suits you to take accurate measurements and discuss your requirements. No pressure, just helpful advice.",
              },
              {
                title: "Honest Pricing",
                desc: "Our quotes are clear and comprehensive with no hidden costs. The price we quote is the price you pay, guaranteed.",
              },
              {
                title: "Quality Installation",
                desc: "Our experienced fitters take pride in their work. We treat every home with respect and always leave properties clean and tidy.",
              },
            ].map((item) => (
              <div key={item.title} className="bg-[#1A1A1A] p-8">
                <h3 className="text-white font-bold mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services Links */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-bold text-[#0A0A0A] mb-6">Our Services</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/windows" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Double Glazing Windows
            </Link>
            <Link to="/doors" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Composite & uPVC Doors
            </Link>
            <Link to="/outbuilds" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Garden Rooms
            </Link>
            <Link to="/recent-work" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              View Our Recent Work
            </Link>
            <Link to="/reviews" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Customer Reviews
            </Link>
            <Link to="/faq" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Window Installation FAQ
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
